"use client";

import { useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query";
import { createContext, PropsWithChildren, useContext } from "react";

import type { getDocManagementInstructions } from "@/src/components/server/get-document-managment-instructions";
import type { getEhrInformation } from "@/src/components/server/get-ehr-information";
import type { getPriorAuthInstructions } from "@/src/components/server/get-prior-auth-instructions";
import type { getReferralAuthInstructions } from "@/src/components/server/get-referral-auth-instructions";
import type { getReferralInstructions } from "@/src/components/server/get-referral-instructions";

export interface ServerComponentsContext {
  getPriorAuthInstructions: typeof getPriorAuthInstructions | undefined;
  getReferralAuthInstructions: typeof getReferralAuthInstructions | undefined;
  getReferralInstructions: typeof getReferralInstructions | undefined;
  getDocManagementInstructions: typeof getDocManagementInstructions | undefined;
  getEhrInformation: typeof getEhrInformation | undefined;
}

export const ServerComponentsContext = createContext<ServerComponentsContext>({
  getPriorAuthInstructions: undefined,
  getReferralAuthInstructions: undefined,
  getReferralInstructions: undefined,
  getDocManagementInstructions: undefined,
  getEhrInformation: undefined,
});

export function useServerComponents() {
  return useContext(ServerComponentsContext);
}

export function useServerComponent<
  Action extends keyof ServerComponentsContext,
  Props extends NonNullable<ServerComponentsContext[Action]>,
>(
  actionName: Action,
  props: Parameters<Props>[0],
  options?: { refetchInterval?: number | false }
): UseQueryResult<React.ReactElement<any> | null> {
  const actions = useServerComponents();
  const action = actions[actionName];

  if (!action) {
    throw new Error(
      `useServerComponent called for action "${action}" which is not set on the ServerComponentsProvider`
    );
  }

  return useQuery({
    queryKey: ["server-components", actionName, props],
    refetchInterval: options?.refetchInterval,
    queryFn: async () => {
      const component = await action(props as any);

      return component;
    },
  });
}

export function useInvalidateServerComponent<Action extends keyof ServerComponentsContext>(
  actionName: Action
): () => void {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: ["server-components", actionName],
    });
  };
}

export function ServerComponentsClientProvider({
  value,
  children,
}: PropsWithChildren<{ value: ServerComponentsContext }>) {
  return <ServerComponentsContext.Provider value={value}>{children}</ServerComponentsContext.Provider>;
}
